export type SnackbarTone = "success" | "info" | "warning" | "error" | "primary";

export type FrontendStudioSettings = {
  brand: {
    desktopTitle: string;
    mobileTitle: string;
    mobileSubtitle: string;
    drawerTitle: string;
    logoUrl: string;
  };
  visuals: {
    appGradientStart: string;
    appGradientEnd: string;
    cardGlassOpacity: number;
  };
  feedback: {
    defaultDurationMs: number;
    presets: Record<
      SnackbarTone,
      {
        title: string;
        icon: string;
      }
    >;
  };
};

const STORAGE_KEY = "vsk_frontend_studio_settings_v1";

export const defaultFrontendStudioSettings: FrontendStudioSettings = {
  brand: {
    desktopTitle: "Vicces Sárga Kutyák",
    mobileTitle: "VSK",
    mobileSubtitle: "Közösségi platform",
    drawerTitle: "Vicces Sárga Kutyák",
    logoUrl: "/assets/vsk_logo_new.png",
  },
  visuals: {
    appGradientStart: "#0f172a",
    appGradientEnd: "#1d4ed8",
    cardGlassOpacity: 0.88,
  },
  feedback: {
    defaultDurationMs: 5200,
    presets: {
      success: { title: "Sikeres művelet", icon: "mdi-check-decagram" },
      info: { title: "Tájékoztatás", icon: "mdi-information-outline" },
      warning: { title: "Figyelmeztetés", icon: "mdi-alert-outline" },
      error: { title: "Hiba történt", icon: "mdi-alert-circle-outline" },
      primary: { title: "Rendszer üzenet", icon: "mdi-bell-ring-outline" },
    },
  },
};

const clampOpacity = (value: number) =>
  Math.max(0.1, Math.min(1, Number(value) || 0.88));
const clampDuration = (value: number) =>
  Math.max(1500, Math.min(20000, Math.round(Number(value) || 5200)));

const safeClone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

export function normalizeFrontendStudioSettings(
  raw: Partial<FrontendStudioSettings> | null | undefined
): FrontendStudioSettings {
  const base = safeClone(defaultFrontendStudioSettings);
  if (!raw || typeof raw !== "object") return base;

  const merged: FrontendStudioSettings = {
    ...base,
    ...raw,
    brand: {
      ...base.brand,
      ...(raw.brand || {}),
    },
    visuals: {
      ...base.visuals,
      ...(raw.visuals || {}),
    },
    feedback: {
      ...base.feedback,
      ...(raw.feedback || {}),
      presets: {
        ...base.feedback.presets,
        ...(raw.feedback?.presets || {}),
      },
    },
  };

  merged.visuals.cardGlassOpacity = clampOpacity(
    merged.visuals.cardGlassOpacity
  );
  merged.feedback.defaultDurationMs = clampDuration(
    merged.feedback.defaultDurationMs
  );
  return merged;
}

export function loadFrontendStudioSettings(): FrontendStudioSettings {
  if (typeof window === "undefined")
    return safeClone(defaultFrontendStudioSettings);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return safeClone(defaultFrontendStudioSettings);
    return normalizeFrontendStudioSettings(JSON.parse(raw));
  } catch {
    return safeClone(defaultFrontendStudioSettings);
  }
}

export function saveFrontendStudioSettings(
  settings: FrontendStudioSettings
): FrontendStudioSettings {
  const normalized = normalizeFrontendStudioSettings(settings);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
}

export function resetFrontendStudioSettings(): FrontendStudioSettings {
  const base = safeClone(defaultFrontendStudioSettings);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(base));
  }
  return base;
}

export function applyFrontendStudioCssVars(settings: FrontendStudioSettings) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty(
    "--vsk-gradient-start",
    String(settings.visuals.appGradientStart || "#0f172a")
  );
  root.style.setProperty(
    "--vsk-gradient-end",
    String(settings.visuals.appGradientEnd || "#1d4ed8")
  );
  root.style.setProperty(
    "--vsk-card-glass-opacity",
    String(clampOpacity(settings.visuals.cardGlassOpacity))
  );
}

export function getSnackbarPreset(
  settings: FrontendStudioSettings,
  color?: string
): { title: string; icon: string } {
  const key = String(color || "primary").toLowerCase() as SnackbarTone;
  const preset =
    settings.feedback.presets[key] || settings.feedback.presets.primary;
  return {
    title: String(
      preset?.title ||
        defaultFrontendStudioSettings.feedback.presets.primary.title
    ),
    icon: String(
      preset?.icon ||
        defaultFrontendStudioSettings.feedback.presets.primary.icon
    ),
  };
}

export { STORAGE_KEY as frontendStudioStorageKey };
